'use client'

import * as React from 'react'
import { ChevronRight, Copy, Check } from 'lucide-react'

import { cn } from '@/lib/utils'

/**
 * Shared "Technical details" disclosure (Plan Phase B4).
 *
 * A collapsed-by-default section that hides forensic/technical fields beneath a
 * plain-language main view. Owned here; consumed by the phishing device drawer,
 * LMS audit/learner drawers, announcement/recipient drawers, and the shared
 * Settings log views — none of them may fork their own copy.
 *
 * Accessibility contract (all required + testable, per B4):
 *  1. real <button> with aria-expanded + aria-controls → panel id.
 *  2. collapsed content is UNMOUNTED (not merely visually hidden) so the raw
 *     dumps are NOT screen-reader / Ctrl-F reachable while closed — the whole
 *     point of the redesign. We conditionally render the panel; we do NOT use
 *     Radix Collapsible (which keeps content mounted).
 *  3. the item count is in the ACCESSIBLE NAME ("Technical details, 21 items").
 *  4. the trigger label is an <h3> heading so the drawer keeps a navigable outline.
 *  5. "Copy all technical details" announces its result via role="status"/aria-live
 *     and falls back to a selectable <textarea> when navigator.clipboard is absent.
 *  7. no collapse animation (instant mount/unmount) ⇒ prefers-reduced-motion safe.
 *  8. focus stays on the trigger; revealed content follows it in DOM order.
 *
 * Note (criterion 6): raw <pre> dumps must carry tabindex=0 + an accessible name —
 * that lives in the CONSUMER's section content, not here.
 */

export interface TechnicalDetailsSection {
  /** Bucket title, e.g. "Network", "Hardware & display", "Raw data". */
  title: string
  /** The bucket's rows/dumps. */
  children: React.ReactNode
}

export interface TechnicalDetailsDisclosureProps {
  sections: TechnicalDetailsSection[]
  /** Number of items — surfaced in the accessible name. */
  itemCount: number
  /** Plain-text of everything, for the "Copy all technical details" button. */
  copyText: string
  /** Defaults to "Technical details". */
  label?: string
  className?: string
}

let uidSeq = 0

export function TechnicalDetailsDisclosure({
  sections,
  itemCount,
  copyText,
  label = 'Technical details',
  className,
}: TechnicalDetailsDisclosureProps) {
  // Never persisted — always closed on open (B4: "state not persisted").
  const [open, setOpen] = React.useState(false)
  const [copyState, setCopyState] = React.useState<'idle' | 'copied' | 'failed'>(
    'idle',
  )
  // Stable ids without useId() churn across the tree.
  const idRef = React.useRef<string>('')
  if (!idRef.current) idRef.current = `tech-details-${(uidSeq += 1)}`
  const panelId = `${idRef.current}-panel`
  const fallbackId = `${idRef.current}-fallback`

  const accessibleName = `${label}, ${itemCount} item${itemCount === 1 ? '' : 's'}`

  const onCopy = React.useCallback(async () => {
    try {
      if (
        typeof navigator !== 'undefined' &&
        navigator.clipboard?.writeText
      ) {
        await navigator.clipboard.writeText(copyText)
        setCopyState('copied')
        window.setTimeout(() => setCopyState('idle'), 2000)
        return
      }
      // Non-secure context / no clipboard API → surface a selectable textarea.
      setCopyState('failed')
    } catch {
      setCopyState('failed')
    }
  }, [copyText])

  return (
    <div className={cn('rounded-lg border bg-muted/30', className)}>
      <h3 className="m-0">
        <button
          type="button"
          aria-expanded={open}
          aria-controls={panelId}
          onClick={() => setOpen((v) => !v)}
          className={cn(
            'flex w-full items-center gap-2 rounded-lg px-3 py-2.5 text-start',
            'text-sm font-medium text-foreground/90 transition-colors',
            'hover:bg-muted/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
          )}
        >
          <ChevronRight
            aria-hidden="true"
            className={cn(
              'h-4 w-4 shrink-0 text-muted-foreground transition-transform motion-reduce:transition-none',
              // Closed, it points along the reading direction; open, it points down either way.
              open ? 'rotate-90' : 'rtl-flip',
            )}
          />
          {/* Visible label + a count chip; the full count is in the accessible
              name via aria-label so screen readers announce "…, N items". */}
          <span aria-hidden="true">{label}</span>
          <span
            aria-hidden="true"
            className="ms-1 rounded-full bg-muted px-1.5 py-0.5 text-[11px] font-normal text-muted-foreground"
          >
            {itemCount}
          </span>
          {/* The accessible name for the whole control. */}
          <span className="sr-only">{accessibleName}</span>
        </button>
      </h3>

      {open && (
        <div id={panelId} className="border-t px-3 py-3">
          <div className="mb-3 flex items-center justify-end">
            <button
              type="button"
              onClick={onCopy}
              className={cn(
                'inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1',
                'text-xs font-medium text-muted-foreground transition-colors',
                'hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
              )}
            >
              {copyState === 'copied' ? (
                <Check aria-hidden="true" className="h-3.5 w-3.5 text-green-600" />
              ) : (
                <Copy aria-hidden="true" className="h-3.5 w-3.5" />
              )}
              Copy all technical details
            </button>
          </div>

          {/* Announce copy result to assistive tech. */}
          <div role="status" aria-live="polite" className="sr-only">
            {copyState === 'copied'
              ? 'Technical details copied to clipboard.'
              : copyState === 'failed'
                ? 'Copy is unavailable here — select the text below to copy it.'
                : ''}
          </div>

          {copyState === 'failed' && (
            <textarea
              id={fallbackId}
              readOnly
              aria-label="Technical details — select all and copy"
              className="mb-3 h-24 w-full resize-y rounded-md border bg-background p-2 font-mono text-[11px]"
              value={copyText}
              onFocus={(e) => e.currentTarget.select()}
            />
          )}

          <div className="space-y-4">
            {sections.map((s) => (
              <section key={s.title} aria-label={s.title}>
                <h4 className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                  {s.title}
                </h4>
                {s.children}
              </section>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
